/**
 * Audio engine: one AudioContext (created on a deliberate user gesture), two independent buses —
 * music and environment — each with its own volume and mute, a shared sleep-fade stage, and a
 * gentle limiter. Nothing plays until the listener enables sound.
 *
 *   music element ─► musicGain ─► musicFade ─┐
 *   world ambience ─► envGain ─► envFade ────┼─► limiter ─► destination
 */

export type AudioUnlockState = 'locked' | 'running' | 'suspended' | 'unavailable';

export interface BusLevels {
  volume: number;
  muted: boolean;
}

type Listener = (s: AudioUnlockState) => void;

/** Perceptual volume curve (slider 0..1 → gain). */
export function sliderToGain(v: number): number {
  const c = Math.max(0, Math.min(1, v));
  return c * c;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private musicGain: GainNode | null = null;
  private envGain: GainNode | null = null;
  private musicFade: GainNode | null = null;
  private envFade: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private listeners = new Set<Listener>();
  private music: BusLevels = { volume: 0.7, muted: false };
  private env: BusLevels = { volume: 0.7, muted: false };
  private stateValue: AudioUnlockState = 'locked';
  private envConsumers = new Set<(ctx: AudioContext, out: AudioNode) => void>();

  get state(): AudioUnlockState {
    return this.stateValue;
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  /** Destination for the music source(s). */
  get musicInput(): AudioNode | null {
    return this.musicGain;
  }

  /** Destination for the world's environmental sound. */
  get envInput(): AudioNode | null {
    return this.envGain;
  }

  onState(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Run `fn` once the environment bus exists (immediately if already unlocked). */
  whenEnvReady(fn: (ctx: AudioContext, out: AudioNode) => void): () => void {
    if (this.ctx && this.envGain) fn(this.ctx, this.envGain);
    else this.envConsumers.add(fn);
    return () => this.envConsumers.delete(fn);
  }

  private setState(s: AudioUnlockState): void {
    if (s === this.stateValue) return;
    this.stateValue = s;
    this.listeners.forEach((fn) => fn(s));
  }

  /** Must be called from a user gesture handler. Safe to call repeatedly. */
  async unlock(): Promise<boolean> {
    if (!this.ctx) {
      const Ctor: typeof AudioContext | undefined =
        typeof window !== 'undefined' ? window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext : undefined;
      if (!Ctor) {
        this.setState('unavailable');
        return false;
      }
      const ctx = new Ctor({ latencyHint: 'playback' });
      this.ctx = ctx;
      this.limiter = ctx.createDynamicsCompressor();
      this.limiter.threshold.value = -6;
      this.limiter.knee.value = 12;
      this.limiter.ratio.value = 4;
      this.limiter.attack.value = 0.02;
      this.limiter.release.value = 0.4;
      this.limiter.connect(ctx.destination);
      this.musicFade = ctx.createGain();
      this.envFade = ctx.createGain();
      this.musicFade.connect(this.limiter);
      this.envFade.connect(this.limiter);
      this.musicGain = ctx.createGain();
      this.envGain = ctx.createGain();
      this.musicGain.connect(this.musicFade);
      this.envGain.connect(this.envFade);
      // Start silent and fade to the configured levels: choosing a world never jumps in loudness.
      this.musicGain.gain.value = 0;
      this.envGain.gain.value = 0;
      this.applyLevels(1.5);
      ctx.addEventListener('statechange', () => this.syncState());
      for (const fn of this.envConsumers) fn(ctx, this.envGain);
      this.envConsumers.clear();
    }
    try {
      if (this.ctx.state !== 'running') await this.ctx.resume();
    } catch {
      /* resume can reject without a gesture; state stays suspended */
    }
    this.syncState();
    return this.ctx.state === 'running';
  }

  private syncState(): void {
    if (!this.ctx) return;
    this.setState(this.ctx.state === 'running' ? 'running' : 'suspended');
  }

  setMusicLevels(l: BusLevels, rampSec = 0.3): void {
    this.music = { ...l };
    this.applyLevels(rampSec);
  }

  setEnvLevels(l: BusLevels, rampSec = 0.3): void {
    this.env = { ...l };
    this.applyLevels(rampSec);
  }

  private applyLevels(rampSec: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.musicGain || !this.envGain) return;
    const t = ctx.currentTime;
    const m = this.music.muted ? 0 : sliderToGain(this.music.volume);
    const e = this.env.muted ? 0 : sliderToGain(this.env.volume);
    for (const [node, v] of [
      [this.musicGain, m],
      [this.envGain, e],
    ] as const) {
      node.gain.cancelScheduledValues(t);
      node.gain.setValueAtTime(node.gain.value, t);
      node.gain.linearRampToValueAtTime(v, t + Math.max(0.01, rampSec));
    }
  }

  /**
   * Sleep fade stage (0..1) for music and/or environment, ramped over `seconds`.
   * Used by the sleep timer; the volume preferences themselves are not changed.
   */
  setFade(target: { music?: number; env?: number }, seconds: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.musicFade || !this.envFade) return;
    const t = ctx.currentTime;
    const ramp = (node: GainNode, v: number) => {
      node.gain.cancelScheduledValues(t);
      node.gain.setValueAtTime(node.gain.value, t);
      node.gain.linearRampToValueAtTime(v, t + Math.max(0.01, seconds));
    };
    if (target.music !== undefined) ramp(this.musicFade, target.music);
    if (target.env !== undefined) ramp(this.envFade, target.env);
  }

  /** Suspend the context entirely (used only if the listener does not want audio while hidden). */
  async suspend(): Promise<void> {
    if (this.ctx && this.ctx.state === 'running') await this.ctx.suspend();
    this.syncState();
  }

  async close(): Promise<void> {
    await this.ctx?.close();
    this.ctx = null;
    this.setState('locked');
  }
}
